import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { CommonButtonRowWidget, UtilityService } from "@eforall/common";
import { DialogService } from "@eforall/dashboard";
import { LanguageId, VolunteerDetails } from "@interfaces";
import { DomainDataService } from "../../../../../../services";
import { VideoDialog, VideoDialogData } from "../../../../../widget";
import { VolunteerDialogDataService } from "../../volunteer-dialog-data.service";

@Component({
	selector: 'lead-volunteer-application-overview-content-dialog-part',
	standalone: true,
	imports: [CommonButtonRowWidget],
	templateUrl: './lead-volunteer-overview-content-dialog.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './lead-volunteer-overview-content-dialog.part.scss'
})
export class LeadVolunteerOverviewContentDialogPart {

	private readonly dialogService = inject(DialogService);
	private readonly domainData = inject(DomainDataService);
	private readonly dialogDataService = inject(VolunteerDialogDataService);
	private readonly util = inject(UtilityService);

	// Expose language IDs for template use
	public readonly englishLanguageId = LanguageId.English;
	public readonly spanishLanguageId = LanguageId.Espanol;

	public readonly volunteerDetails = computed(() => {
		const volunteer = this.dialogDataService.volunteerDetails();
		if (!volunteer) return null;

		const languages: string[] = [];
		if (volunteer.willVolunteerInEnglish) languages.push('English');
		if (volunteer.willVolunteerInSpanish) languages.push('Spanish');

		const interests: string[] = [];
		if (volunteer.mentorInterest) interests.push('Mentoring');
		if (volunteer.inClassInterest) interests.push('In-Class Topic Specialist');
		if (volunteer.officeHoursInterest) interests.push('Office Hours Topic Specialist');

		const expertises: string[] = volunteer.expertises
			.map(e => this.domainData.data().expertisesMap[e]?.name)
			.filter(name => typeof name === 'string');

		const socialLinks = volunteer.socialLinks
			.map(link => ({
				webLinkTypeId: link.webLinkTypeId,
				type: this.domainData.data().webLinkTypesMap[link.webLinkTypeId]?.name,
				url: link.url
			}))

		return {
			...volunteer,
			languages,
			interests: interests.length === 0 ? ['N/A'] : interests,
			expertises: expertises.length === 0 ? ['N/A'] : expertises,
			socialLinks,
			createdUTC: this.util.date.formatUTC(volunteer.createdUTC, 'MMM, DD YYYY', 'No Time', 'en-US'),
			interviewDateUTC: volunteer.interviewDateUTC ? this.util.date.formatUTC(volunteer.interviewDateUTC, 'MMM, DD YYYY', 'H:MM AM EST', 'en-US') : "Not Scheduled",
			status: this.domainData.data().volunteerStatusesMap[volunteer.volunteerStatusId]?.name,
			englishVideoStatus: this.getEnglishVideoStatus(volunteer),
			spanishVideoStatus: this.getSpanishVideoStatus(volunteer),
		};
	});
	public readonly isLeadVolunteer = computed(() => this.dialogDataService.isLeadVolunteer());

	backToVolunteerDetails() {
		this.dialogDataService.overviewContentView.set('volunteer');
	}

	async openVideoDialog(languageId: LanguageId) {
		const details = this.volunteerDetails();
		if (!details?.person.id) return;

		const hasVideo = languageId === LanguageId.English ? details.hasEnVideo : details.hasEsVideo;
		if (hasVideo) {
			const dialogData: VideoDialogData = {
				type: 'volunteer',
				personId: details.person.id,
				uniqueVideoId: languageId
			};
			this.dialogService.showCustom(VideoDialog, { data: dialogData }, 500, 550);
		}
	}

	private formatVideoStatus(hasVideo: boolean, uploadedUTC: number | undefined): string {
		if (!hasVideo) return 'Not Added';
		if (hasVideo && !uploadedUTC) return 'Uploaded';
		if (hasVideo && uploadedUTC) {
			const formattedDate = this.util.date.formatUTC(uploadedUTC, 'MMM, DD YYYY', 'H:MM AM EST', 'en-US');
			return `Uploaded: ${formattedDate}`;
		}
		return 'Not Added';
	}

	private getEnglishVideoStatus(volunteer: VolunteerDetails): string {
		return this.formatVideoStatus(volunteer.hasEnVideo, volunteer.englishVideoUploadedUTC);
	}

	private getSpanishVideoStatus(volunteer: VolunteerDetails): string {
		return this.formatVideoStatus(volunteer.hasEsVideo, volunteer.spanishVideoUploadedUTC);
	}
}